/**
 * A small rich text editor for a letter sign-off. Takes the sign-off as stored (with the signature
 * token) and reports changes back the same way, showing the signature image while editing.
 *
 * onChange gets `edited`: true when the change came from the user, false when it was only the
 * editor settling on HTML it was given. The letter screen relies on that to tell a sign-off the user
 * changed from one that is still the default.
 *
 * Given on-save, the toolbar also gets a save icon, shown while can-save is true.
 */
app.component("signOffEditor", {
	templateUrl: "app/src/js/components/signOff/signOffEditor.html",
	controller: "signOffEditorController",
	bindings: {
		html: "<",
		signatureDataUrl: "<",
		placeholderLabel: "<",
		readOnly: "<",
		canSave: "<",
		saveTitle: "<",
		onChange: "&",
		onSave: "&",
	},
});

app.controller("signOffEditorController", [
	"$element",
	"$attrs",
	"$sce",
	"$timeout",
	"SignOffService",
	function ($element, $attrs, $sce, $timeout, SignOffService) {
		const ctrl = this;

		// The last sign-off this editor reported or was given, so a value coming back in from the
		// parent is not mistaken for a new one.
		let currentHtml = "";

		// Set once the user does something in the editor, so the changes that follow are theirs.
		let userIsEditing = false;

		function render() {
			ctrl.editorHtml = SignOffService.toDisplayHtml(currentHtml, ctrl.signatureDataUrl, ctrl.placeholderLabel);
			ctrl.previewHtml = $sce.trustAsHtml(ctrl.editorHtml);
		}

		// The toolbar's save icon, once summernote has drawn it.
		let saveButton = null;

		function updateSaveButton() {
			if (saveButton) {
				saveButton.toggle(!!ctrl.canSave).attr("title", ctrl.saveTitle || "Save");
			}
		}

		function markUserEditing() {
			userIsEditing = true;
		}

		ctrl.$onChanges = function (changes) {
			if (changes.html && (changes.html.currentValue || "") !== currentHtml) {
				currentHtml = changes.html.currentValue || "";
				userIsEditing = false;
				render();
			} else if (changes.signatureDataUrl || changes.placeholderLabel) {
				render();
			}
			if (changes.canSave || changes.saveTitle) {
				updateSaveButton();
			}
		};

		ctrl.$postLink = function () {
			// Typing, pasting and formatting commands raise "input"; the toolbar covers the rest (font
			// size, colour). Setting the editor's HTML from code raises neither.
			$element.on("input paste", ".note-editable", markUserEditing);
			$element.on("mousedown", ".note-toolbar", markUserEditing);
		};

		ctrl.$onDestroy = function () {
			$element.off("input paste", ".note-editable", markUserEditing);
			$element.off("mousedown", ".note-toolbar", markUserEditing);
		};

		ctrl.onEditorChange = function (contents) {
			const html = SignOffService.toStoredHtml(contents);
			if (html === currentHtml && !userIsEditing) {
				return;
			}
			currentHtml = html;
			ctrl.onChange({ html, edited: userIsEditing });
		};

		function SignatureButton(context) {
			const ui = $.summernote.ui;
			const button = ui.button({
				contents: '<i class="fa fa-pencil-square-o"/> Signature',
				tooltip: "Insert your handwritten signature",
				click: function () {
					markUserEditing();
					context.invoke("editor.insertNode", SignOffService.createSignatureImage(ctrl.signatureDataUrl, ctrl.placeholderLabel));
				},
			});
			return button.render();
		}

		function SaveButton() {
			const ui = $.summernote.ui;
			const button = ui.button({
				contents: '<i class="fa fa-floppy-o"/>',
				click: function () {
					// Summernote's click is outside Angular, so the save has to be started inside a digest.
					$timeout(() => ctrl.onSave());
				},
			});
			saveButton = button.render();
			updateSaveButton();
			return saveButton;
		}

		const toolbar = [
			["insert", ["signOffSignature"]],
			["style", ["bold", "italic", "underline", "clear"]],
			["textsize", ["fontsize"]],
			["fontclr", ["color"]],
			["alignment", ["paragraph"]],
		];
		if (angular.isDefined($attrs.onSave)) {
			toolbar.push(["save", ["signOffSave"]]);
		}

		ctrl.options = {
			height: 150,
			disableResizeEditor: true,
			placeholder: "Kind regards, ...",
			fontSizes: ["10", "11", "12", "14", "16", "18"],
			toolbar,
			buttons: {
				signOffSignature: SignatureButton,
				signOffSave: SaveButton,
			},
		};
	},
]);
